import type { NavigationGuardWithThis } from 'vue-router';
import { authenticatedSettingsLoadPromise } from '../composables/use-settings-load-when-authenticated';
import { getAdministrationPolicy } from '../composables/use-administration-features';
import { settingsLoadPromise } from '../composables/use-update-settings';
import { useHAlerts } from '../composables/use-alerts';
import { useHTranslate } from '../composables/use-translate';
import {
  ADMIN_FEATURE_DISABLED_KEY,
  ADMIN_FEATURE_NOT_AVAILABLE_MULTITENANT_KEY,
  ADMIN_FEATURE_REQUIRES_DOMAIN_ADMIN_KEY,
  adminRouteUnavailableReason,
  isAdministrationPolicyRoute,
  type AdministrationUnavailableReason,
} from '../utils/administration-features';

export const ADMIN_FEATURE_UNAVAILABLE_ALERT_ID = 'admin-feature-unavailable';
export const ADMIN_FEATURE_FALLBACK_PATH = '/';

const UNAVAILABLE_MESSAGE_KEYS: Record<AdministrationUnavailableReason, string> = {
  disabled: ADMIN_FEATURE_DISABLED_KEY,
  multitenant: ADMIN_FEATURE_NOT_AVAILABLE_MULTITENANT_KEY,
  platform: ADMIN_FEATURE_REQUIRES_DOMAIN_ADMIN_KEY,
};

export function notifyAdministrationUnavailable(reason: AdministrationUnavailableReason) {
  const { hAddAlert } = useHAlerts();

  hAddAlert({
    id: ADMIN_FEATURE_UNAVAILABLE_ALERT_ID,
    type: 'warning',
    title: useHTranslate(UNAVAILABLE_MESSAGE_KEYS[reason]),
    closable: true,
    timeout: 8_000,
  });
}

/**
 * Keeps disabled features (`administration_disabled`), the guides, forms and
 * dashboards administration in multitenant mode, and in multitenant mode the
 * pages only a platform administrator (`domainadmin`) may use, from mounting,
 * also from a bookmark or reload. It waits for the authenticated settings and
 * the user, so the page and its requests do not start before the mode and the
 * roles are known. An unavailable page redirects home (`/`), which is never
 * matched, so the redirect cannot loop. A page that becomes unavailable while
 * it is open is handled by `useAdministrationRedirect`.
 */
export const administrationFeaturesGuard: NavigationGuardWithThis<undefined> = async (to) => {
  if (!isAdministrationPolicyRoute(to)) {
    return;
  }

  await settingsLoadPromise;
  await authenticatedSettingsLoadPromise;

  const reason = adminRouteUnavailableReason(to, getAdministrationPolicy());
  if (!reason) {
    return;
  }

  notifyAdministrationUnavailable(reason);
  return { path: ADMIN_FEATURE_FALLBACK_PATH };
};
